/**
 * Hook for managing mail address (mailbox) settings.
 *
 * Encapsulates all GraphQL calls, local form state, and validation
 * for the MailboxSettings component.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useState, useCallback, useMemo } from 'react';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import {
  GetMailAddressDocument,
  CreateMailAddressDocument,
  EnableMailAddressDocument,
  DisableMailAddressDocument,
  DeleteMailAddressDocument
} from '@/graphql';
import { validateMailPrefix, type PrefixValidationError } from '../utils/mail-prefix-validation';
import { getMailIngestDomain } from '../utils/mail-domain';

/** Shared refetch options — every mutation refreshes the same query */
const REFETCH_MAIL_ADDRESS = {
  refetchQueries: [{ query: GetMailAddressDocument }],
  awaitRefetchQueries: true
};

interface UseMailAddressSettingsOptions {
  /** Called once after a mailbox is successfully created */
  onCreated?: () => void;
}

export function useMailAddressSettings(options: UseMailAddressSettingsOptions = {}) {
  const { onCreated } = options;
  const t = useFeatureTranslation('systemSettings.mailbox');
  const { showSuccess, showError } = useToastHelpers();

  // ── Data fetching ───────────────────────────────────────────────────
  const {
    data,
    loading: queryLoading,
    error: queryError
  } = useQuery(GetMailAddressDocument, {
    fetchPolicy: 'network-only'
  });

  const mailAddress = data?.mailAddress ?? null;
  const hasMailbox = mailAddress !== null;

  // ── Mutations ───────────────────────────────────────────────────────
  const [createMailAddress, { loading: creating }] = useMutation(CreateMailAddressDocument);
  const [enableMailAddress, { loading: enabling }] = useMutation(EnableMailAddressDocument);
  const [disableMailAddress, { loading: disabling }] = useMutation(DisableMailAddressDocument);
  const [deleteMailAddress, { loading: deleting }] = useMutation(DeleteMailAddressDocument);

  // ── Local form state (creation mode) ────────────────────────────────
  const [prefix, setPrefix] = useState('');
  const [validationError, setValidationError] = useState<PrefixValidationError | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const domain = useMemo(() => getMailIngestDomain(), []);

  // The server appends a random suffix, so the chosen prefix alone is never the real address.
  // Showing `faktury-…@domain` promises the shape without inventing digits the server will pick.
  const previewAddress = useMemo(() => {
    if (!prefix.trim()) return '';
    return `${prefix.toLowerCase().trim()}-…@${domain}`;
  }, [prefix, domain]);

  // ── Validation error → translated message ───────────────────────────
  const validationErrorMessage = useMemo(() => {
    if (!validationError) return null;
    const errorKeyMap: Record<PrefixValidationError, string> = {
      required: 'validation.required',
      tooShort: 'validation.tooShort',
      tooLong: 'validation.tooLong',
      invalidFormat: 'validation.invalidFormat',
      blacklisted: 'validation.blacklisted'
    };
    return t(errorKeyMap[validationError]);
  }, [validationError, t]);

  // ── Handlers ────────────────────────────────────────────────────────
  const handlePrefixChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setPrefix(value);
      setServerError(null);
      if (validationError) {
        setValidationError(null);
      }
    },
    [validationError]
  );

  const handleCreate = useCallback(async () => {
    const error = validateMailPrefix(prefix);
    if (error) {
      setValidationError(error);
      return;
    }

    setServerError(null);
    try {
      const result = await createMailAddress({
        variables: { input: { prefix: prefix.toLowerCase().trim() } },
        ...REFETCH_MAIL_ADDRESS,
        errorPolicy: 'none'
      });
      if (!result.data?.createMailAddress) {
        setServerError(t('feedback.createError'));
        showError(t('feedback.createError'));
        return;
      }
      showSuccess(t('feedback.createSuccess'));
      setPrefix('');
      onCreated?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('feedback.createError');
      if (message.includes('already has a mail address')) {
        setServerError(t('validation.alreadyConfigured'));
      } else {
        setServerError(message);
        showError(t('feedback.createError'));
      }
    }
  }, [prefix, createMailAddress, showSuccess, showError, t, onCreated]);

  const handleToggleEnabled = useCallback(async () => {
    if (!mailAddress) return;
    try {
      if (mailAddress.enabled) {
        await disableMailAddress({
          variables: { id: mailAddress.id },
          ...REFETCH_MAIL_ADDRESS
        });
        showSuccess(t('feedback.disableSuccess'));
      } else {
        await enableMailAddress({
          variables: { id: mailAddress.id },
          ...REFETCH_MAIL_ADDRESS
        });
        showSuccess(t('feedback.enableSuccess'));
      }
    } catch {
      showError(t('feedback.toggleError'));
    }
  }, [mailAddress, enableMailAddress, disableMailAddress, showSuccess, showError, t]);

  const handleDelete = useCallback(async () => {
    if (!mailAddress) return;
    try {
      await deleteMailAddress({
        variables: { id: mailAddress.id },
        ...REFETCH_MAIL_ADDRESS
      });
      showSuccess(t('feedback.deleteSuccess'));
    } catch {
      showError(t('feedback.deleteError'));
    }
  }, [mailAddress, deleteMailAddress, showSuccess, showError, t]);

  const handleCopyAddress = useCallback(async () => {
    if (!mailAddress?.fullAddress) return;
    try {
      await navigator.clipboard.writeText(mailAddress.fullAddress);
      showSuccess(t('feedback.copied'));
    } catch {
      showError(t('feedback.copyError'));
    }
  }, [mailAddress?.fullAddress, showSuccess, showError, t]);

  const isBusy = creating || enabling || disabling || deleting;

  return {
    t,
    // Query state
    mailAddress,
    hasMailbox,
    loading: queryLoading,
    error: queryError,
    // Form state (creation)
    prefix,
    domain,
    previewAddress,
    validationErrorMessage,
    serverError,
    // Handlers
    handlePrefixChange,
    handleCreate,
    handleToggleEnabled,
    handleDelete,
    handleCopyAddress,
    // Loading states
    creating,
    isBusy
  };
}
